// نمودارهای سبکِ زنجیره تأمین — SVG/CSS خالص، بدون کتابخانهٔ خارجی، سازگار با RTL
// رنگ‌ها از توکن‌های CSS (var) می‌آیند تا هر دو تمِ روشن/تیره خودکار درست شوند.
import React from "react";
import { JALALI_MONTHS } from "../../utils/dateUtils";
import { faNum } from "../../utils/faNumberFormat";

// نگاشتِ نقش → متغیرِ CSS (تعریف‌شده در gharchak-theme.css برای هر دو تم)
const V = {
  yarn: "var(--stage-yarn)",
  raw: "var(--stage-raw)",
  dyed: "var(--stage-dyed)",
  received: "var(--stage-received)",
  good: "var(--success)",
  bad: "var(--danger)",
  warn: "var(--warning)",
  grid: "var(--border)",
  muted: "var(--text-mute)",
  sec: "var(--text-sec)",
  text: "var(--text)",
};
const fa = (n) => faNum(Number(n || 0), { maximumFractionDigits: 3 });
const faPct = (v) => (v == null ? "—" : `${faNum(Number(v), { maximumFractionDigits: 2 })}٪`);

// تگِ وضعیتِ سبک (سالم/هشدار/بدون‌داده) با رنگِ توکن‌محور
function StatusTag({ ok, noData, okLabel = "در حدِ مجاز ✓", badLabel = "بیش از تلورانس ✗" }) {
  const c = noData ? V.muted : ok ? V.good : V.bad;
  return (
    <span style={{
      fontSize: 11.5, fontWeight: 700, color: c,
      background: `color-mix(in srgb, ${c} 15%, transparent)`,
      padding: "3px 10px", borderRadius: 99, whiteSpace: "nowrap",
    }}>
      {noData ? "بدون داده" : ok ? okLabel : badLabel}
    </span>
  );
}

// ───────────────── جریانِ مرحله‌ای (قیف) ─────────────────
// transitions: [{key,title,in_label,in_kg,out_label,out_kg,loss_kg,loss_pct,tolerance_pct,within_tolerance,has_data}]
export function StageFlow({ transitions = [], overall, onDrill }) {
  const blocks = [...transitions];
  const Block = ({ t, isOverall, idx = 0 }) => {
    const max = Math.max(t.in_kg, t.out_kg, 1);
    const ok = t.within_tolerance;
    const noData = !t.has_data;
    const inW = Math.max(2, Math.min(100, (t.in_kg / max) * 100));
    const outW = Math.max(2, Math.min(100, (t.out_kg / max) * 100));
    const statusColor = noData ? V.grid : ok ? V.good : V.bad;
    const inColor = isOverall ? V.yarn : V.yarn;
    const outColor = isOverall ? V.received : V.raw;
    return (
      <div style={{
        padding: 14, borderRadius: 12, marginBottom: 10,
        background: isOverall ? "color-mix(in srgb, var(--success) 6%, var(--surface-2))" : "var(--surface-2)",
        border: `1px solid ${isOverall ? V.received : "var(--border)"}`,
        borderInlineStart: `4px solid ${statusColor}`,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 8, flexWrap: "wrap" }}>
          <b style={{ fontSize: 13.5, color: V.text }}>{t.title}</b>
          <StatusTag ok={ok} noData={noData} />
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
          <span style={{ width: 130, fontSize: 12, color: V.sec, textAlign: "start" }}>{t.in_label}</span>
          <div className="gx-flow-track">
            <div className="gx-flow-fill" style={{ width: `${inW}%`, background: inColor, animationDelay: `${0.3 + idx * 0.1}s` }} />
          </div>
          <b style={{ width: 90, textAlign: "start", fontSize: 13, color: V.text, fontFeatureSettings: "'tnum'" }}>{fa(t.in_kg)} کیلو</b>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ width: 130, fontSize: 12, color: V.sec, textAlign: "start" }}>{t.out_label}</span>
          <div className="gx-flow-track">
            <div className="gx-flow-fill" style={{ width: `${outW}%`, background: outColor, animationDelay: `${0.4 + idx * 0.1}s` }} />
          </div>
          <b style={{ width: 90, textAlign: "start", fontSize: 13, color: V.text, fontFeatureSettings: "'tnum'" }}>{fa(t.out_kg)} کیلو</b>
        </div>

        {!noData && (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10, flexWrap: "wrap", gap: 6 }}>
            <span style={{ fontSize: 12.5 }}>
              <span style={{ color: t.loss_kg > 0 ? V.bad : V.good, fontWeight: 700 }}>
                {t.loss_kg >= 0 ? "افت: " : "افزایش: "}{fa(Math.abs(t.loss_kg))} کیلو ({faPct(Math.abs(t.loss_pct))})
              </span>
              <span style={{ color: V.muted, marginInlineStart: 10 }}>
                مجاز: {faPct(t.tolerance_pct)} ({fa(t.allowed_loss_kg)} کیلو)
              </span>
            </span>
            {onDrill && t.drill_out && (
              <button onClick={() => onDrill(t)}
                style={{ border: "none", background: "transparent", color: "var(--brand-soft)", cursor: "pointer", fontSize: 12.5, fontWeight: 600, fontFamily: "inherit", padding: 0 }}>
                مشاهدهٔ رویدادها →
              </button>
            )}
          </div>
        )}
      </div>
    );
  };
  return (
    <div>
      {blocks.map((t, i) => <Block key={t.key} t={t} idx={i} />)}
      {overall && <Block t={overall} isOverall idx={blocks.length} />}
    </div>
  );
}

// ───────────────── گیجِ خطیِ افت در برابر تلورانس (HTML/CSS — کاملاً RTL) ─────────────────
// نوار از راست (شروع) پر می‌شود؛ تیکِ نارنجی = آستانهٔ تلورانس. متن‌ها HTML‌اند تا bidi نشکند.
export function LossGauge({ pct, tol }) {
  const hasData = pct != null;
  const p = hasData ? Math.max(0, Number(pct)) : 0;
  const max = Math.max(tol * 2, p * 1.25, 1);
  const pctW = (Math.min(p, max) / max) * 100;
  const tolX = (Math.min(tol, max) / max) * 100;
  const over = hasData && Number(pct) > tol;
  const color = !hasData ? V.muted : over ? V.bad : V.good;
  return (
    <div style={{ direction: "rtl", width: "100%" }}>
      <div className="gx-gauge-track"
        title={hasData ? `افت ${faPct(pct)} — تلورانسِ مجاز ${faPct(tol)}` : "بدون داده"}>
        {hasData && (
          <div className="gx-gauge-fill" style={{ width: `${pctW}%`, background: color }} />
        )}
        {/* تیکِ آستانهٔ تلورانس */}
        <div className="gx-gauge-tick" style={{ insetInlineStart: `calc(${tolX}% - 1px)` }} />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: V.muted, marginTop: 4 }}>
        <span>۰</span>
        <span style={{ color: V.warn }}>تلورانسِ مجاز {faPct(tol)}</span>
      </div>
    </div>
  );
}

// ───────────────── نمودارِ روندِ ماهانه (میله‌ای گروهی) ─────────────────
// data: [{year,month,yarn_purchased,raw_sent,dyed_received,...}]
export function TrendChart({ data = [] }) {
  if (!data.length) return <div style={{ color: V.muted, textAlign: "center", padding: 20 }}>داده‌ای نیست</div>;
  const W = Math.max(320, data.length * 100), H = 210, padB = 44, padT = 16, padX = 12;
  const innerH = H - padB - padT;
  const max = Math.max(1, ...data.flatMap((d) => [d.yarn_purchased, d.raw_sent, d.dyed_received]));
  const groupW = (W - padX * 2) / data.length;
  const series = [
    { key: "yarn_purchased", color: V.yarn, label: "نخِ خرید" },
    { key: "raw_sent", color: V.raw, label: "خامِ ارسالی" },
    { key: "dyed_received", color: V.received, label: "رنگ‌شدهٔ دریافتی" },
  ];
  const bw = Math.min(18, (groupW - 16) / 3);
  const y = (v) => padT + innerH - (v / max) * innerH;
  const baseY = padT + innerH;
  return (
    <div>
      <div style={{ display: "flex", gap: 18, flexWrap: "wrap", marginBottom: 12 }}>
        {series.map((s) => (
          <span key={s.key} style={{ fontSize: 12, color: V.sec, display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 11, height: 11, borderRadius: 3, background: s.color, display: "inline-block" }} />{s.label}
          </span>
        ))}
      </div>
      <div style={{ overflowX: "auto" }}>
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} style={{ minWidth: W, direction: "ltr", overflow: "visible" }} role="img">
          {[0, 0.5, 1].map((g) => (
            <line key={g} x1={padX} x2={W - padX} y1={y(max * g)} y2={y(max * g)}
              stroke={g === 0 ? "var(--border)" : "var(--surface-2)"} strokeWidth={1} />
          ))}
          {data.map((d, i) => {
            const gx = padX + i * groupW + 8;
            return (
              <g key={i}>
                {series.map((s, si) => {
                  const v = d[s.key] || 0;
                  const h = Math.max(0, baseY - y(v));
                  return (
                    <rect key={s.key} className="gx-bar" x={gx + si * (bw + 3)} y={y(v)} width={bw} height={h}
                      rx={2} fill={s.color} style={{ animationDelay: `${0.3 + i * 0.08 + si * 0.06}s` }}>
                      <title>{`${s.label}: ${fa(v)} کیلو`}</title>
                    </rect>
                  );
                })}
                <text x={gx + (bw * 3) / 2} y={H - 22} fontSize="12" fill="var(--text-sec)" textAnchor="middle" fontFamily="Peyda, Tahoma, sans-serif">
                  {JALALI_MONTHS[(d.month - 1) % 12]}
                </text>
                <text x={gx + (bw * 3) / 2} y={H - 6} fontSize="10" fill="var(--text-mute)" textAnchor="middle" fontFamily="Peyda, Tahoma, sans-serif">
                  {faNum(Number(d.year), { useGrouping: false })}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
}
